import {
  SheetNode,
  SheetSkillsList,
  SheetSkill,
  SheetImportError,
} from './types'
import { getTabValuesByTitle } from 'infrastructure/googleDrive'
import { getGoogleDriveFolderId } from 'infrastructure/googleDrive/urlParser'
import { directTreeAccessUrl } from 'infrastructure/api'
import { NavigableNodeTypes, NodeType } from 'shared/types/ingestion'

/**
 * Validates if a string is a valid ID (non-empty)
 */
function isValidId(id: string): boolean {
  return id.trim().length > 0
}

/**
 * Validates if a string is a valid numeric ID
 */
function isValidNumericId(id: string): boolean {
  const num = parseInt(id, 10)
  return !isNaN(num) && id === num.toString() && num > 0
}

/**
 * Reads a Google Sheet and parses it to extract the structure:
 * Dictionary of node_ids to SheetNodes, each containing SheetSkillsLists, each containing SheetSkills
 * Validates the structure and collects errors.
 *
 * @param spreadsheetId - The Google Sheet ID
 * @param accessToken - Google OAuth access token
 * @param treeId - Tree ID for generating links
 * @param versionId - Version ID for generating links
 * @returns Promise resolving to an object with nodesDict (Record of node_id to SheetNode) and errors
 */
export async function importStandardsFromSheet(
  spreadsheetId: string,
  accessToken: string,
  treeId: string,
  versionId: string,
): Promise<{
  nodeList: SheetNode[]
  errors: SheetImportError[]
}> {
  // Extract the spreadsheet ID from URL if needed
  const cleanSpreadsheetId = getGoogleDriveFolderId(spreadsheetId)

  // Read all values from the first sheet using the utility function
  const data = await getTabValuesByTitle({
    spreadsheetId: cleanSpreadsheetId,
    title: 'Sheet1',
    accessToken,
  })

  const rows = data.values || []
  const errors: SheetImportError[] = []

  if (rows.length === 0) {
    return { nodeList: [], errors: [] }
  }

  // Skip header row (row 1)
  const dataRows = rows.slice(1)

  const nodeList: SheetNode[] = []
  const nodesDict: Record<string, SheetNode> = {}
  let currentNode: SheetNode | null = null
  let currentSkillsList: SheetSkillsList | null = null

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i]
    const rowNumber = i + 2 // +2 because we skip header (row 1) and arrays are 0-indexed

    if (row.length < 3) {
      errors.push({
        row: rowNumber,
        message: 'Row must have at least 3 columns',
        data: row.join(', '),
      })
      continue
    }

    const typeOrStandardId = row[0]?.trim() || ''
    const titleOrState = row[1]?.trim() || ''
    const nodeIdOrStandardName = row[2]?.trim() || ''

    // Check if this is a standard_id (numeric)
    const standardId = parseInt(typeOrStandardId, 10)
    const isStandardRow =
      !isNaN(standardId) && typeOrStandardId === standardId.toString()

    if (isStandardRow) {
      // This is a standard/skill row
      // Validation: must follow a skills_list
      if (!currentNode) {
        errors.push({
          row: rowNumber,
          message: 'Skill row must follow a node row',
          data: `skill_id: ${standardId}`,
        })
        continue
      }
      if (!currentSkillsList) {
        errors.push({
          row: rowNumber,
          message: 'Skill row must follow a skills_list row',
          data: `skill_id: ${standardId}`,
        })
        continue
      }
      if (!isValidNumericId(typeOrStandardId)) {
        errors.push({
          row: rowNumber,
          message: 'Invalid skill_id (must be a positive integer)',
          data: `skill_id: ${typeOrStandardId}`,
        })
        continue
      }
      if (!isValidId(nodeIdOrStandardName)) {
        errors.push({
          row: rowNumber,
          message: 'Invalid skill name (must be non-empty)',
          data: `name: ${nodeIdOrStandardName}`,
        })
        continue
      }

      const skill: SheetSkill = {
        skill_id: standardId,
        name: nodeIdOrStandardName || titleOrState || '',
      }
      currentSkillsList.skills.push(skill)
    } else if (typeOrStandardId === 'skills_list') {
      // This is a skills_list row
      // Validation: must follow a node (or another skills_list)
      if (!currentNode) {
        errors.push({
          row: rowNumber,
          message: 'Skills list row must follow a node row',
          data: `skills_list_id: ${nodeIdOrStandardName}`,
        })
        continue
      }

      // Validation: third column must be either "NEW" or a valid id
      const isNewSkillsList = nodeIdOrStandardName.toUpperCase() === 'NEW'
      if (!isNewSkillsList && !isValidId(nodeIdOrStandardName)) {
        errors.push({
          row: rowNumber,
          message: 'Skills list id must be "NEW" or a valid id',
          data: `skills_list_id: ${nodeIdOrStandardName}`,
        })
        continue
      }

      // Save previous skills list if it exists
      if (currentSkillsList) {
        // Add to skillsLists if nodeid is valid (existing), otherwise add to newSkillsLists (new)
        // Empty nodeid means it was "NEW" (new skills list)
        if (isValidId(currentSkillsList.nodeid)) {
          currentNode.skillsLists.push(currentSkillsList)
        } else {
          currentNode.newSkillsLists.push(currentSkillsList)
        }
      }

      // Create new skills list
      // Use "NEW" for new skills lists, otherwise use the provided id
      currentSkillsList = {
        nodeid: isNewSkillsList ? '' : nodeIdOrStandardName,
        title: titleOrState,
        skills: [],
      }
    } else if (typeOrStandardId && nodeIdOrStandardName) {
      // This is a node row (any other type)
      // Validation: must follow a node, skills_list, or skill
      // (nodes can follow other nodes, or after skills_list/skills)
      if (!isValidId(nodeIdOrStandardName)) {
        errors.push({
          row: rowNumber,
          message: 'Invalid node nodeid (must be non-empty)',
          data: `nodeid: ${nodeIdOrStandardName}`,
        })
        continue
      }
      if (!isValidId(typeOrStandardId)) {
        errors.push({
          row: rowNumber,
          message: 'Invalid node type (must be non-empty)',
          data: `type: ${typeOrStandardId}`,
        })
        continue
      }

      // Save previous node if it exists
      if (currentNode) {
        // Save the last skills list if it exists
        if (currentSkillsList) {
          // Add to skillsLists if nodeid is valid (existing), otherwise add to newSkillsLists (new)
          // Empty nodeid means it was "NEW" (new skills list)
          if (isValidId(currentSkillsList.nodeid)) {
            currentNode.skillsLists.push(currentSkillsList)
          } else {
            currentNode.newSkillsLists.push(currentSkillsList)
          }
          currentSkillsList = null
        }
        // Add to nodeList and nodesDict
        nodeList.push(currentNode)
        nodesDict[currentNode.node_id] = currentNode
      }
      // Create new node
      // Check if this is a navigable node type for link generation
      const isNavigableType = NavigableNodeTypes.includes(
        typeOrStandardId as NodeType,
      )
      const isQuestion = typeOrStandardId === 'question'
      let link: string = ''
      if (isNavigableType || isQuestion) {
        // For questions, we still use their own nodeId for link (no parent tracking)
        link = directTreeAccessUrl({
          treeId,
          versionId,
          nodeId: nodeIdOrStandardName,
        })
      }

      currentNode = {
        node_id: nodeIdOrStandardName,
        type: typeOrStandardId,
        title: titleOrState,
        skillsLists: [],
        newSkillsLists: [],
        link,
      }
      currentSkillsList = null
    } else {
      // Unknown row type
      errors.push({
        row: rowNumber,
        message: 'Unknown row type or missing required fields',
        data: `type: ${typeOrStandardId}, id: ${nodeIdOrStandardName}`,
      })
    }
  }

  // Don't forget to add the last node and skills list
  if (currentNode) {
    if (currentSkillsList) {
      // Add to skillsLists if nodeid is valid (existing), otherwise add to newSkillsLists (new)
      // Empty nodeid means it was "NEW" (new skills list)
      if (isValidId(currentSkillsList.nodeid)) {
        currentNode.skillsLists.push(currentSkillsList)
      } else {
        currentNode.newSkillsLists.push(currentSkillsList)
      }
    }
    // Add to nodeList and nodesDict
    nodeList.push(currentNode)
    nodesDict[currentNode.node_id] = currentNode
  }

  return {
    nodeList: errors.length === 0 ? nodeList : [],
    errors,
  }
}
